/**
 * The main menu — the screen the game loads into.
 *
 * Not to be confused with the *menu*, which is the overlay that can be summoned
 * at any point and holds the settings. See `GameMenu`. These were one word for
 * two things for a long time, including on a button in the corner of the game
 * labelled "menu" that meant "leave what you are doing".
 *
 * The galaxy behind, the title over it, and one button. Everything else a menu
 * usually carries — settings, saves, a difficulty picker — either does not
 * exist yet or lives in the overlay, which can be reached from here *and* from
 * the middle of a flight.
 *
 * The starmap is the real one, zoomed out and left alone. It is already the
 * best-looking thing in the project and it is *the world you are about to fly
 * around in*, so there is nothing a still image would add except a second thing
 * to keep in step.
 *
 * ── Signed out ──────────────────────────────────────────────────────────────
 *
 * The menu still draws. A locked door with the lights on behind it is a much
 * better argument for signing in than an error page, and the galaxy is worth
 * looking at whether or not you have an account.
 */

import { HUD_BACKING, HUD_DIM, HUD_EDGE, HUD_ORANGE, HUD_TEXT } from './FlightHud';
import type { PlayerIdentity } from '../net/identity';

export interface MainMenuProps {
  /** Null while signing in, or when there is nobody signed in. */
  player: PlayerIdentity | null;
  /** What to say instead of the play button. Null when play is available. */
  blocked: string | null;
  /** Where play would take you, for the button to say so. */
  resumeLabel: string;
  /**
   * Where the ship was left, when it was left somewhere.
   *
   * Shown because the first question on coming back is "where was I", and the
   * answer is already known — asking a player to press play to find out is a
   * needless step.
   */
  lastSeen: { system: string; holding: string | null } | null;
  /**
   * What is waiting to be collected, if anything.
   *
   * Shown *before* it is claimed, because pressing play is what claims it — so
   * this is the offer rather than a receipt, and it is the whole reason somebody
   * comes back to a tab they left open. Zero means nothing waiting.
   */
  pending?: { daily: number; weekly: number };

  /**
   * Open the menu, which is where the settings live.
   *
   * They used to be here — a music dial and a tips switch in the corner of this
   * screen — which meant they could only be changed *before* starting, and that
   * turning the music down mid-flight meant abandoning the flight. One surface,
   * summonable from anywhere, and this opens the same one.
   */
  onOpenMenu?: () => void;

  /** Debug tools, for accounts that have them. */
  admin: boolean;
  onPlay: () => void;
  onOpenAdmin: () => void;
}

export function MainMenu({
  player,
  blocked,
  resumeLabel,
  lastSeen,
  pending = { daily: 0, weekly: 0 },
  onOpenMenu,
  admin,
  onPlay,
  onOpenAdmin,
}: MainMenuProps) {
  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 26,
        // Deepened towards the middle so the title has something to sit on
        // without hiding the map behind it.
        background:
          'radial-gradient(ellipse at center, rgba(6,3,2,0.86) 0%, rgba(6,3,2,0.55) 45%, rgba(6,3,2,0.2) 100%)',
        pointerEvents: 'none',
      }}
    >
      <img
        src="/games/interspacial/interspacial_logo.png"
        alt="Interspacial"
        style={{
          width: 'min(560px, 62vw)',
          imageRendering: 'auto',
          filter: 'drop-shadow(0 6px 30px rgba(255,138,61,0.25))',
        }}
      />

      {/* Who is flying. Blank rather than guessed while the handshake is in
          flight — a name that appears and then changes is worse than one that
          arrives late. */}
      <div style={{ height: 34, textAlign: 'center' }}>
        {player && (
          <>
            <div style={{ fontSize: 15, color: HUD_TEXT, letterSpacing: 0.5 }}>
              {player.username}
            </div>
            <div
              style={{
                fontSize: 11,
                color: HUD_DIM,
                letterSpacing: 1.2,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {player.displayID}
            </div>
          </>
        )}
      </div>

      {/* Where you were. Fixed height so the button does not move between a
          first run and a returning one. */}
      <div style={{ height: 30, textAlign: 'center' }}>
        {lastSeen && (
          <>
            <div style={{ fontSize: 10, color: HUD_DIM, letterSpacing: 1.6, textTransform: 'uppercase' }}>
              last seen
            </div>
            <div style={{ fontSize: 13, color: HUD_TEXT }}>
              {lastSeen.system}
              {lastSeen.holding && (
                <span style={{ color: HUD_ORANGE }}> · held by {lastSeen.holding}</span>
              )}
            </div>
          </>
        )}
      </div>

      {/* What is waiting, above the button that collects it.
          Deliberately phrased as an invitation rather than a notification: it
          is not a thing that has happened, it is a reason to press play. */}
      {pending.daily + pending.weekly > 0 && (
        <div
          style={{
            fontSize: 12,
            color: HUD_ORANGE,
            textAlign: 'center',
            marginBottom: 10,
            letterSpacing: 0.4,
          }}
        >
          hop in to receive your {pending.daily + pending.weekly} linelit{' '}
          {pending.weekly > 0 && pending.daily > 0
            ? 'daily + weekly bonus'
            : pending.weekly > 0
              ? 'weekly bonus'
              : 'daily bonus'}
        </div>
      )}

      {blocked ? (
        <div
          style={{
            ...PLATE,
            borderColor: 'rgba(255,179,71,0.5)',
            color: '#ffb347',
            pointerEvents: 'auto',
            textAlign: 'center',
            maxWidth: 420,
            lineHeight: 1.5,
          }}
        >
          {blocked}
        </div>
      ) : (
        <PlayButton label={resumeLabel} onClick={onPlay} />
      )}

      {/* Settings, and everything else that is not starting the game.
          Bottom right rather than anywhere near the play button: these are
          preferences, not decisions anybody arrives with, and putting them in
          the path of the one thing everybody presses would be asking a question
          nobody came to answer. */}
      {onOpenMenu && (
        <button
          type="button"
          onClick={onOpenMenu}
          style={{
            position: 'absolute',
            bottom: 52,
            right: 16,
            fontSize: 10,
            letterSpacing: 1.4,
            textTransform: 'uppercase',
            color: HUD_TEXT,
            border: `1px solid ${HUD_EDGE}`,
            background: HUD_BACKING,
            padding: '7px 12px',
            font: 'inherit',
            cursor: 'pointer',
            pointerEvents: 'auto',
          }}
        >
          settings
        </button>
      )}

      {/* Admin. Present rather than hidden behind a key combination, because
          the point of a debug affordance is being able to find it. */}
      {admin && (
        <button
          type="button"
          onClick={onOpenAdmin}
          style={{
            position: 'absolute',
            bottom: 16,
            left: 16,
            fontSize: 10,
            letterSpacing: 1.4,
            textTransform: 'uppercase',
            color: HUD_ORANGE,
            border: `1px solid ${HUD_ORANGE}`,
            background: HUD_BACKING,
            padding: '7px 12px',
            font: 'inherit',
            cursor: 'pointer',
            pointerEvents: 'auto',
          }}
        >
          admin console
        </button>
      )}

      {/* The way out.
          The game is one page inside chaostree, reached from its games list, and
          until now there was no way back to it that was not the browser's own
          back button -- which, after a session of pushed history, is not one
          press. An anchor rather than a button because it is a link: it gets the
          middle-click, the context menu and the status bar for free, and none of
          that is worth reimplementing.

          Absolute, not relative to the app's base: the app is served under
          /games/interspacial/, so `../` would depend on a trailing slash that is
          not guaranteed. */}
      <a
        href="/games/"
        style={{
          position: 'absolute',
          bottom: 16,
          right: 16,
          fontSize: 10,
          letterSpacing: 1.4,
          textTransform: 'uppercase',
          color: HUD_DIM,
          textDecoration: 'none',
          border: `1px solid ${HUD_EDGE}`,
          background: HUD_BACKING,
          padding: '7px 12px',
          font: 'inherit',
          cursor: 'pointer',
          pointerEvents: 'auto',
        }}
      >
        back to games
      </a>
    </div>
  );
}

/**
 * The one control.
 *
 * Built like the console switches rather than like a web button: the same
 * sunken housing, the same orange, the same travel when you press it. The menu
 * is the first thing anyone sees, and it should already look like the cockpit
 * they are about to be sitting in.
 */
function PlayButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        position: 'relative',
        width: 260,
        height: 74,
        border: `1px solid ${HUD_ORANGE}`,
        background: 'linear-gradient(180deg, rgba(255,138,61,0.3), rgba(120,52,16,0.35))',
        boxShadow: 'inset 0 2px 6px rgba(0,0,0,0.6), 0 0 26px rgba(255,138,61,0.25)',
        color: '#fff1e4',
        font: 'inherit',
        fontSize: 22,
        letterSpacing: 6,
        textTransform: 'uppercase',
        textIndent: 6,
        cursor: 'pointer',
        pointerEvents: 'auto',
        transition: 'background 120ms linear, box-shadow 120ms linear',
      }}
      onMouseEnter={(event) => {
        event.currentTarget.style.background =
          'linear-gradient(180deg, rgba(255,168,105,0.42), rgba(150,66,20,0.45))';
        event.currentTarget.style.boxShadow =
          'inset 0 2px 6px rgba(0,0,0,0.6), 0 0 38px rgba(255,138,61,0.45)';
      }}
      onMouseLeave={(event) => {
        event.currentTarget.style.background =
          'linear-gradient(180deg, rgba(255,138,61,0.3), rgba(120,52,16,0.35))';
        event.currentTarget.style.boxShadow =
          'inset 0 2px 6px rgba(0,0,0,0.6), 0 0 26px rgba(255,138,61,0.25)';
      }}
    >
      play
      {/* Where it puts you, in the small print. A player who left mid-flight
          should know that is where they are going back to. */}
      <span
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 7,
          fontSize: 9,
          letterSpacing: 1.6,
          textIndent: 0,
          color: 'rgba(255,241,228,0.55)',
        }}
      >
        {label}
      </span>
    </button>
  );
}

const PLATE: React.CSSProperties = {
  background: HUD_BACKING,
  border: `1px solid ${HUD_EDGE}`,
  color: HUD_TEXT,
  padding: '14px 20px',
  fontSize: 13,
};
